import {
    Box,
    Button,
    Flex, Icon, Image, Modal, ModalBody, ModalCloseButton,
    ModalContent, ModalFooter, ModalHeader,
    ModalOverlay,
    SimpleGrid,
    SkeletonCircle,
    SkeletonText,
    Text, useDisclosure, useToast
} from "@chakra-ui/react";
import React, {useEffect, useState} from "react";
import {deleteData, getData, postData} from "../../../services/api";
import RentalItem from "../../../components/rental/RentalItem";
import {NavLink, useHistory} from "react-router-dom";
import Card from "../../../components/card/Card";
import {IoWarningOutline} from "react-icons/io5";
import {IoMdAdd} from "react-icons/io";
import {RENTAL_STATUS} from "../../../variables/rentalStatus";

export default function Rentals() {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const toast = useToast();
    const history = useHistory();

    const [user, setUser] = useState(null);
    const [rentals, setRentals] = useState(null);
    const [loading, setLoading] = useState('rentals');
    const [error, setError] = useState(null);
    const [selectedRental, setSelectedRental] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {

        // Define an async function to fetch data
        const fetchData = async () => {
            try {
                const result = await getData('/api/rentals');
                setRentals(result.data);
                setLoading(null);
            } catch (error) {
                setError(error.message);
                setLoading(null);
            }
        };
        fetchData();

        const getUserData = async () => {
            try {
                const result = await getData('/api/user/info');
                setUser(result);
            } catch (error) {
                setError(error.message);
            }
        };
        getUserData();

    }, []);

    const handleSelectRental = (rental) => {
        setSelectedRental(rental)
        onOpen()
    }

    const handleDeleteClick = () => {
        setConfirmDelete(true)
    };

    const handleAbortDeleteClick = () => {
        setConfirmDelete(false)
    }

    const handleConfirmDeleteClick = async () => {
        try {
            setLoading('delete')
            await deleteData('/api/rentals/' + selectedRental?.id);
            setRentals(rentals.filter(rental => rental.id !== selectedRental?.id));
            setLoading(null)
            onClose()

            toast({
                title: "Inserat wurde gelöscht.",
                description: `Das Inserat (${selectedRental?.title}) wurde erfolgreich gelöscht.`,
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            setError(error.message);
        }
    };

    const handleAddRental = async () => {
        try {
            setLoading('add')
            const result = await postData('/api/rentals');
            if(result && result.id){
                history.push(`/user/add-rental/${result.id}`);
            }
            setLoading(null)
        } catch (error) {
            setError(error.message);
        }
    };

    return (
        <Box pt={"80px"}>
            {loading === 'rentals' ? (
                <SimpleGrid columns={{base: 1, md: 3}} gap='20px'>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                    <Box padding='6' boxShadow='lg' bg='white'>
                        <SkeletonCircle size='10'/>
                        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='2'/>
                    </Box>
                </SimpleGrid>
            ) : error ? (
                <div>Error: {error}</div>
            ) : rentals ? (
                <Box>
                    <SimpleGrid columns={{base: 1, md: 3}} gap='20px'>
                        {rentals.map(rental => (
                            <div key={rental.id} style={{marginBottom: '20px', cursor: 'pointer'}}
                                 onClick={() => handleSelectRental(rental)}>
                                <RentalItem
                                    rental={rental}
                                />
                            </div>
                        ))}
                    </SimpleGrid>
                    {user?.has_address ?
                        <Card p='20px' bgColor={'brand.500'} w={{base: "100%", lg: '50%'}}>
                            <Flex justifyContent={'space-between'}>
                                <div>
                                    <Text
                                        fontSize={{base: "24px", md: "34px"}}
                                        color='white'
                                        mb='14px'
                                        maxW={{
                                            base: "100%",
                                            md: "64%",
                                            lg: "46%",
                                            xl: "70%",
                                            "2xl": "50%",
                                            "3xl": "42%",
                                        }}
                                        fontWeight='700'
                                        lineHeight={{base: "32px", md: "42px"}}>
                                        Stelle ein neues Inserat hier ein
                                    </Text>
                                    <Button
                                        bg='white'
                                        color='black'
                                        _hover={{bg: "whiteAlpha.900"}}
                                        _active={{bg: "white"}}
                                        _focus={{bg: "white"}}
                                        fontWeight='500'
                                        fontSize='14px'
                                        py='20px'
                                        px='27'
                                        me='38px'
                                        onClick={() => handleAddRental()}
                                        isDisabled={loading === 'add'}
                                        isLoading={loading === 'add'}
                                    >
                                        Neues Inserat anlegen
                                    </Button>
                                </div>
                                <Icon as={IoMdAdd} color={'green.500'} w='40px' h='40px' ms={5}/>
                            </Flex>
                        </Card> :
                        <Card p='20px' bgColor={'blue'} w={'50%'}>
                            <Flex justifyContent={'space-between'}>
                                <div>
                                    <Text
                                        fontSize={{base: "24px", md: "34px"}}
                                        color='white'
                                        mb='14px'
                                        maxW={{
                                            base: "100%",
                                            md: "64%",
                                            lg: "46%",
                                            xl: "70%",
                                            "2xl": "50%",
                                            "3xl": "42%",
                                        }}
                                        fontWeight='700'
                                        lineHeight={{base: "32px", md: "42px"}}>
                                        Vervollständige dein Profil um Inserate einstellen zu können
                                    </Text>
                                    <NavLink
                                        to={"/user/profile"}
                                        mt={{
                                            base: "0px",
                                            md: "10px",
                                            lg: "0px",
                                            xl: "10px",
                                            "2xl": "0px",
                                        }}>
                                        <Button
                                            bg='white'
                                            color='black'
                                            _hover={{bg: "whiteAlpha.900"}}
                                            _active={{bg: "white"}}
                                            _focus={{bg: "white"}}
                                            fontWeight='500'
                                            fontSize='14px'
                                            py='20px'
                                            px='27'
                                            me='38px'>
                                            Profil vervollständigen
                                        </Button>
                                    </NavLink>
                                </div>
                                <Icon as={IoWarningOutline} color={'orange.500'} w='40px' h='40px' ms={5}/>
                            </Flex>
                        </Card>
                    }
                </Box>
            ) : (
                <div>Keine Daten gefunden</div>
            )}

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay/>
                <ModalContent bgColor="white">
                    <ModalHeader>Dein Inserat</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        <Box>
                            <Image
                                src={selectedRental?.pictures[0]?.url}
                                w={"50%"}
                                h={"50%"}
                                borderRadius='20px'
                                objectFit='cover'
                                aspectRatio='1'
                                bgColor='grey'
                                ml={'auto'}
                                mr={'auto'}
                                mb={5}
                            />
                            <Text textAlign={'center'} fontSize='sm' fontWeight='600'>{selectedRental?.title}</Text>
                            <Text textAlign={'center'} color='secondaryGray.600' fontSize='sm'
                                  fontWeight='500'>{selectedRental?.place}</Text>
                        </Box>
                    </ModalBody>
                    <ModalFooter display={'block'}>
                        {
                            selectedRental?.status === RENTAL_STATUS.UNFINISHED ?
                                <NavLink
                                    to={"/user/add-rental/" + selectedRental?.id}>
                                    <Button
                                        variant='darkBrand'
                                        color='white'
                                        fontSize='sm'
                                        width='100%'
                                        mb={'5'}
                                    >
                                        Inserat bearbeiten
                                    </Button>
                                </NavLink> :
                                <NavLink
                                    to={"/user/rental/" + selectedRental?.id}>
                                    <Button
                                        variant='darkBrand'
                                        color='white'
                                        fontSize='sm'
                                        width='100%'
                                        mb={'5'}
                                    >
                                        Zum Inserat
                                    </Button>
                                </NavLink>
                        }
                        {confirmDelete ?
                            <Box>
                                <Button variant='outline' mb={2} onClick={handleAbortDeleteClick} width='100%'
                                        fontSize='sm'>
                                    Abbrechen
                                </Button>
                                <Button variant='solid' colorScheme='red' onClick={handleConfirmDeleteClick}
                                        width='100%' isLoading={loading === 'delete'}
                                        fontSize='sm'>
                                    Wirklich löschen?
                                </Button>
                                <Text mt={2}>Bitte beachte: Wenn du das Inserat löschst, werden alle Google-Rankings und
                                    auch deine erreichten Bewertungen dauerhaft gelöscht.</Text>
                            </Box> :
                            <Button variant='outline' onClick={handleDeleteClick} width='100%'
                                    fontSize='sm'>
                                Inserat löschen
                            </Button>
                        }
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
